// NPM Imports
import React, { Component } from "react";
import PropTypes from "prop-types";
import { Document, Page } from "react-pdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withRouter } from "react-router-dom";
import { Loader, Toastr, Button } from "@revvsales/components";

// Service Imports
import {
  createDocFromGlobalTemplates,
  cloneGlobalTemplate,
  changeTemplateStatusService,
} from "../../../../actions/DocumentTemplateActions/templateService";

// Internal Imports
import { GlobalUtils, AmplitudeUtils } from "../../../../utils";
import { PATHS, LOADING_MSG, EVENTS, PERMISSIONS } from "../../../../constants";

// Destructuring
const {
  writeDataToLocalStorage,
  handleApiErrMsg,
  checkUserPermission,
} = GlobalUtils;
const { amplitudeLogEvent } = AmplitudeUtils;

class GlobalTemplatesPreview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cloneDocBtnDisabled: false,
      createDocBtnDisabled: false,
      docIndex: null,
      isLoading: true,
      numPages: null,
      pageNumber: 1,
      templateIDs: {},
    };
    this.focusPreview = React.createRef();
  }

  componentDidMount() {
    this.focusPreview.current.focus();
    this._preventAppScroll();
    this.setState({
      docIndex: this.props.index,
    });
  }

  componentWillUnmount() {
    // Reset the css properties to default
    const htmlElement = document.documentElement;
    htmlElement.style.width = "";
    htmlElement.style.position = "";
    htmlElement.style.top = "";
    htmlElement.style.overflow = "";
  }

  // Prevent App from scrolling when popup is displayed
  _preventAppScroll = () => {
    const htmlElement = document.documentElement;
    htmlElement.style.width = "calc(100% - 0px)";
    htmlElement.style.position = "fixed";
    htmlElement.style.top = "0px";
    htmlElement.style.overflow = "hidden";
  };

  _onDocumentLoadSuccess = ({ numPages }) => {
    this.setState({ numPages, isLoading: false });
  };

  _onDocNavigation = type => {
    amplitudeLogEvent(EVENTS.EVENT_NAMES.TEMPLATE_PREVIEW_NAVIGATION, {
      OldTemplateID: this.props.list[this.state.docIndex].id,
      OldTemplateName: this.props.list[this.state.docIndex].title,
      Navigation: type === "next" ? "Next" : "Previous",
    });
    this.setState(prevState => ({
      docIndex:
        type === "next" ? prevState.docIndex + 1 : prevState.docIndex - 1,
      templateID: null,
    }));
  };

  _createDocument = () => {
    const amplitudeObject = {
      InitiatedFrom: "Template Library",
      TemplateID: this.props.list[this.state.docIndex].id,
      TemplateName: this.props.list[this.state.docIndex].title,
      SequenceNumber: this.state.docIndex + 1,
    };
    this.setState({ createDocBtnDisabled: true });
    const templateID = this.props.list[this.state.docIndex].id;
    createDocFromGlobalTemplates(templateID)
      .then(res => {
        writeDataToLocalStorage("documentPageSource", "templates");
        this.props.history.push(`${PATHS.DOCUMENTS_PATH}/${res.doc_no}`);
        amplitudeLogEvent(EVENTS.EVENT_NAMES.DOCUMENT_CREATED, amplitudeObject);
      })
      .catch(err => {
        this.setState({
          createDocBtnDisabled: false,
        });
        Toastr.showToastr({
          type: "failure",
          message: handleApiErrMsg(err),
          timeout: 3000,
        });
        amplitudeLogEvent(EVENTS.EVENT_NAMES.DOCUMENT_CREATED, {
          Error: handleApiErrMsg(err),
          InitiatedFrom: "Template Library",
        });
        console.error("GlobalTemplatesPreview => _createDocument", err);
      });
  };

  _cloneDocument = () => {
    amplitudeLogEvent(EVENTS.EVENT_NAMES.ADD_TEMPLATE_BUTTON_CLICKED, {
      TemplateID: this.props.list[this.state.docIndex].id,
      TemplateName: this.props.list[this.state.docIndex].title,
      SequenceNumber: this.state.docIndex + 1,
    });

    this.setState({ cloneDocBtnDisabled: true });
    const templateID = this.props.list[this.state.docIndex].id;
    cloneGlobalTemplate(templateID)
      .then(res => {
        this.setState(prevState => ({
          cloneDocBtnDisabled: false,
          templateIDs: {
            ...prevState.templateIDs,
            [prevState.docIndex]: res.template_id,
          },
        }));
        Toastr.showToastr({
          type: "success",
          message: "Template has been added successfully",
          timeout: 3000,
        });
      })
      .catch(err => {
        this.setState({
          cloneDocBtnDisabled: false,
        });
        Toastr.showToastr({
          type: "failure",
          message: handleApiErrMsg(err),
          timeout: 3000,
        });
        amplitudeLogEvent(EVENTS.EVENT_NAMES.ADD_TEMPLATE_BUTTON_CLICKED, {
          Error: handleApiErrMsg(err),
        });
        console.error("GlobalTemplatesPreview => _cloneDocument", err);
      });
  };

  _openDocTemplate = () => {
    const templateID = this.state.templateIDs[this.state.docIndex];
    const templateStatusPayload = { template_type: "", status: "DRAFT" };
    changeTemplateStatusService(templateID, templateStatusPayload)
      .then(() => {
        this.props.history.push(`${PATHS.DOCSTEMPLATE_PATH}/${templateID}`);
      })
      .catch(err => {
        console.error("GlobalTemplatesPreview => _openDocTemplate", err);
      });
  };

  _handleKeyboard = e => {
    if (e.which === 27) {
      this.props.previewClose(null);
    }
    if (e.which === 37 && this.state.docIndex > 0) {
      this._onDocNavigation();
    }
    if (e.which === 39 && this.state.docIndex < this.props.list.length - 1) {
      this._onDocNavigation("next");
    }
  };

  render() {
    const {
      cloneDocBtnDisabled,
      createDocBtnDisabled,
      docIndex,
      numPages,
      pageNumber,
      templateIDs,
    } = this.state;
    return (
      <div
        className="globaltemplatespreview-wrapper"
        onKeyDown={e => this._handleKeyboard(e)}
        role="button"
        ref={this.focusPreview}
        tabIndex={0}>
        {this.state.isLoading && (
          <Loader label={LOADING_MSG.GLOBAL_TEMPLATE_PREVIEW} theme="dark" />
        )}
        {this.state.docIndex !== null && (
          <div className="globaltemplatespreview-content">
            <section className="globaltemplatespreview-header-section">
              <div className="globaltemplatespreview-doc-title">
                {this.props.list[docIndex].title}
              </div>

              <div className="globaltemplatespreview-actions-section">
                {checkUserPermission(
                  PERMISSIONS.TEMPLATES.UPDATE_TEMPLATES,
                ) && (
                  <Button
                    data-test="global-templatepreview-addandedit-button"
                    className="globaltemplatespreview-add-template-btn"
                    disabled={cloneDocBtnDisabled}
                    isLoading={cloneDocBtnDisabled}
                    onClick={
                      templateIDs[docIndex]
                        ? this._openDocTemplate
                        : this._cloneDocument
                    }>
                    {templateIDs[docIndex]
                      ? "Open and edit template"
                      : "Add to my templates"}
                  </Button>
                )}
                <Button
                  disabled={createDocBtnDisabled}
                  data-test="global-templatepreview-create-button"
                  isLoading={createDocBtnDisabled}
                  onClick={this._createDocument}>
                  Create document from template
                </Button>
                <i
                  className="globaltemplatespreview-close-ico"
                  onClick={() => this.props.previewClose(null)}
                  onKeyPress={() => null}
                  role="button"
                  tabIndex="0">
                  <FontAwesomeIcon icon={["far", "times"]} />
                </i>
              </div>
            </section>
            <section className="globaltemplatespreview-preview-section">
              {docIndex !== 0 && (
                <i
                  className="globaltemplatespreview-left-arrow"
                  onClick={this._onDocNavigation}
                  onKeyPress={() => null}
                  role="button"
                  tabIndex="0">
                  <FontAwesomeIcon icon={["far", "angle-left"]} />
                </i>
              )}
              <Document
                file={this.props.list[docIndex].pdf_url}
                onLoadSuccess={this._onDocumentLoadSuccess}>
                {Array.from(new Array(numPages), (el, index) => (
                  <Page key={`page_${index + 1}`} pageNumber={index + 1} />
                ))}
              </Document>
              <p className="globaltemplate-page-no">
                Page {pageNumber} of {numPages}
              </p>
              {docIndex !== this.props.list.length - 1 && (
                <i
                  className="globaltemplatespreview-right-arrow"
                  onClick={() => this._onDocNavigation("next")}
                  onKeyPress={() => null}
                  role="button"
                  tabIndex="0">
                  <FontAwesomeIcon icon={["far", "angle-right"]} />
                </i>
              )}
            </section>
          </div>
        )}
      </div>
    );
  }
}

GlobalTemplatesPreview.propTypes = {
  index: PropTypes.number.isRequired,
  list: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  previewClose: PropTypes.func.isRequired,
};

export default withRouter(GlobalTemplatesPreview);



// WEBPACK FOOTER //
// ./src/components/TemplateV2/GlobalTemplates/GlobalTemplatesPreview/index.js